import { useQueries, useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataItem } from '../../../../../@types/DataItem';
import { PriceExtraction } from '../../../../../@types/IPA/PriceExtraction';
import * as service from '../../../../../components/FilterSection/services';
import {
  getCascadingFilterParams,
  getCascadingFilterParamsSignature,
  hasCascadingFilterParams,
  isCascadingFilterKey,
} from '../../../../../components/NewFilterBox/data/cascadingFilters';
import { useDebounce } from '../../../../../hooks';
import { selectorSegmentos } from '../../../../../reducers/gerenciadorPrecos/segmentos';
import {
  RESET_EXTRACTION_FILTERS_CACHE,
  selectorIpaExtractionFiltersCache,
  SET_EXTRACTION_FILTERS_CACHE_BY_KEY,
} from '../../../../../reducers/ipa/extraction/filters/cache';
import { selectorIpaExtractionFiltersData, SET_EXTRACTION_FILTERS_DATA_BY_KEY } from '../../../../../reducers/ipa/extraction/filters/data';
import {
  RESET_EXTRACTION_FILTERS_QUERIES,
  selectorIpaExtractionFiltersQueries,
  SET_EXTRACTION_FILTERS_QUERIES_BY_KEY,
} from '../../../../../reducers/ipa/extraction/filters/queries';
import {
  RESET_EXTRACTION_FILTERS_VALUE,
  selectorIpaExtractionFiltersValues,
  SET_EXTRACTION_FILTERS_VALUE_BY_KEY,
} from '../../../../../reducers/ipa/extraction/filters/values';
import { checkIfIsLoading } from '../../../../../utils';

type Key = PriceExtraction['filtersKeys'];

export const useFilters = () => {
  const dispatch = useDispatch();
  const debounce = useDebounce();
  const data = useSelector(selectorIpaExtractionFiltersData);
  const value = useSelector(selectorIpaExtractionFiltersValues);
  const queries = useSelector(selectorIpaExtractionFiltersQueries);
  const cache = useSelector(selectorIpaExtractionFiltersCache);
  const cattegories = useSelector(selectorSegmentos);
  const valuesRef = useRef(value);
  const initialCascadingDataRef = useRef<Partial<Record<Key, DataItem[]>>>({});
  const lastLoadedCascadingSignatureRef = useRef<Partial<Record<Key, string>>>({});
  // Mesmo fluxo do filtro de produtos do Gerenciador (ProductsPill): o texto colado vira a busca,
  // vai para o backend com isModal e tudo o que volta é somado à seleção atual. Guarda o próprio texto
  // colado (e não só uma flag) para que só a resposta dessa busca seja tratada como colagem.
  const [pastedProductsQuery, setPastedProductsQuery] = useState<string | null>(null);
  const isPastedProductsQuery = pastedProductsQuery !== null && (queries.productIds ?? '') === pastedProductsQuery;

  useEffect(() => {
    valuesRef.current = value;
  }, [value]);

  const getFilterCascadingParams = useCallback((key: Key) => {
    if (!isCascadingFilterKey(key)) return {};
    return getCascadingFilterParams(valuesRef.current, key);
  }, []);

  const handleSuccess = useCallback(
    (key: Key, data: Array<DataItem | service.SegmentData>, options?: { query?: string; cascadingSignature?: string; hasCascadingParams?: boolean }) => {
      const newData = _.uniqBy([...(cache[key] ?? []), ...data], 'value');

      if (isCascadingFilterKey(key)) {
        if (!options?.query && !options?.hasCascadingParams) {
          initialCascadingDataRef.current[key] = data as DataItem[];
        }

        if (!options?.query) {
          lastLoadedCascadingSignatureRef.current[key] = options?.cascadingSignature ?? '';
        }
      }

      dispatch(SET_EXTRACTION_FILTERS_DATA_BY_KEY({ key, data: newData as DataItem[] }));
    },
    [dispatch, cache],
  );

  const handlePastedProductsSuccess = useCallback(
    (data: DataItem[]) => {
      const uniqueData = _.uniqBy([...(cache.productIds ?? []), ...data], 'value');
      const value = uniqueData.map(item => item.value);
      valuesRef.current = { ...valuesRef.current, productIds: value };
      dispatch(SET_EXTRACTION_FILTERS_VALUE_BY_KEY({ key: 'productIds', value }));
      dispatch(SET_EXTRACTION_FILTERS_CACHE_BY_KEY({ key: 'productIds', data: uniqueData }));
      setPastedProductsQuery(null);
    },
    [dispatch, cache],
  );

  const loadingProducts = useQuery({
    queryKey: ['ipa.extraction.filters.productIds', queries.productIds],
    queryFn: async () => {
      const cascadingParams = getFilterCascadingParams('productIds');
      const options = isPastedProductsQuery ? { ...cascadingParams, isModal: true } : cascadingParams;
      try {
        return await service.listProducts(queries?.productIds ?? '', options);
      } catch (error) {
        setPastedProductsQuery(null);
        throw error;
      }
    },
    onSuccess: data => {
      const cascadingParams = getFilterCascadingParams('productIds');
      handleSuccess('productIds', data, {
        query: queries.productIds,
        cascadingSignature: getCascadingFilterParamsSignature(cascadingParams),
        hasCascadingParams: hasCascadingFilterParams(cascadingParams),
      });
      if (isPastedProductsQuery) handlePastedProductsSuccess(data);
    },
  });

  const loadingProductFamilyIds = useQuery({
    queryKey: ['ipa.filter.productFamilyIds', queries.productFamilyIds],
    queryFn: () => service.listFamilies(queries.productFamilyIds ?? '', getFilterCascadingParams('productFamilyIds')),
    onSuccess: data => {
      const cascadingParams = getFilterCascadingParams('productFamilyIds');
      handleSuccess('productFamilyIds', data, {
        query: queries.productFamilyIds,
        cascadingSignature: getCascadingFilterParamsSignature(cascadingParams),
        hasCascadingParams: hasCascadingFilterParams(cascadingParams),
      });
    },
  });

  const loadingStoreIds = useQuery({
    queryKey: ['ipa.filter.storeIds', queries.storeIds],
    queryFn: () => service.listStores(queries.storeIds),
    onSuccess: data => handleSuccess('storeIds', data),
  });

  const loadingClusters = useQuery({
    queryKey: ['ipa.filter.clusters', queries.clusters],
    queryFn: () => service.listClusters(queries.clusters),
    onSuccess: data => handleSuccess('clusters', data),
  });

  const loadingSegmentos = useQuery({
    queryKey: ['ipa.filter.segmentos'],
    queryFn: service.listaSegmentacao,
    onSuccess: data => handleSuccess('segmentos', data),
  });

  const loadingStoreTypes = useQuery({
    queryKey: ['ipa.filter.storeTypes', queries.storeTypes],
    queryFn: () => service.listStoreType(queries.storeTypes || ''),
    onSuccess: data => handleSuccess('storeTypes', data as unknown as DataItem[]),
  });

  const handleSearch = useCallback(
    (key: Key, query: string) => {
      debounce(() => dispatch(SET_EXTRACTION_FILTERS_QUERIES_BY_KEY({ key, query })));
    },
    [dispatch, debounce],
  );

  const handleConfirmPasteProducts = useCallback(
    (query: string) => {
      if (!query.length) return;
      setPastedProductsQuery(query);
      handleSearch('productIds', query);
    },
    [handleSearch],
  );

  const handleClean = useCallback(
    (key: Key) => {
      const nextValues = { ...valuesRef.current, [key]: [] };
      valuesRef.current = nextValues;
      dispatch(SET_EXTRACTION_FILTERS_QUERIES_BY_KEY({ key, query: '' }));
      dispatch(SET_EXTRACTION_FILTERS_VALUE_BY_KEY({ key, value: [] }));
      dispatch(SET_EXTRACTION_FILTERS_CACHE_BY_KEY({ key, data: [] }));

      if (!isCascadingFilterKey(key)) return;

      const cascadingParams = getCascadingFilterParams(nextValues, key);
      if (!hasCascadingFilterParams(cascadingParams) && initialCascadingDataRef.current[key]) {
        lastLoadedCascadingSignatureRef.current[key] = '';
        dispatch(SET_EXTRACTION_FILTERS_DATA_BY_KEY({ key, data: initialCascadingDataRef.current[key] ?? [] }));
      }
    },
    [dispatch],
  );

  const handleCleanAll = useCallback(() => {
    valuesRef.current = {};
    lastLoadedCascadingSignatureRef.current = {};
    dispatch(RESET_EXTRACTION_FILTERS_CACHE());
    dispatch(RESET_EXTRACTION_FILTERS_VALUE());
    dispatch(RESET_EXTRACTION_FILTERS_QUERIES());

    (Object.keys(initialCascadingDataRef.current) as Key[]).forEach(key => {
      if (initialCascadingDataRef.current[key]) {
        dispatch(SET_EXTRACTION_FILTERS_DATA_BY_KEY({ key, data: initialCascadingDataRef.current[key] ?? [] }));
      }
    });
  }, [dispatch]);

  const handleChange = useCallback(
    (key: Key, value: string[]) => {
      valuesRef.current = {
        ...valuesRef.current,
        [key]: value,
      };
      dispatch(SET_EXTRACTION_FILTERS_VALUE_BY_KEY({ key, value }));
      if (key === 'segmentos') return;
      const cache = data[key]?.filter(item => value.includes(item.value));
      dispatch(SET_EXTRACTION_FILTERS_CACHE_BY_KEY({ key, data: cache ?? [] }));
    },
    [dispatch, data],
  );

  const handleSelectAll = useCallback(
    (key: Key, checked: boolean) => {
      if (key === 'segmentos') return;
      let newData: string[] = [];
      if (checked) {
        newData = data[key]?.map(({ value }) => value) ?? [];
      }
      valuesRef.current = {
        ...valuesRef.current,
        [key]: newData,
      };
      dispatch(SET_EXTRACTION_FILTERS_VALUE_BY_KEY({ key, value: newData }));
    },
    [dispatch, data],
  );

  const results = useQueries({
    queries: cattegories.map(({ value: key, level }) => ({
      queryKey: ['ipa.filter.segmentos', key, queries[key]],
      queryFn: () => service.listSegment(queries[key] ?? '', level, getFilterCascadingParams(key)),
      onSuccess: (data: DataItem[]) => {
        const cascadingParams = getFilterCascadingParams(key);
        handleSuccess(key, data, {
          query: queries[key],
          cascadingSignature: getCascadingFilterParamsSignature(cascadingParams),
          hasCascadingParams: hasCascadingFilterParams(cascadingParams),
        });
      },
      enabled: !!cattegories.length,
    })),
  });

  const handleOpen = useCallback(
    async (key: Key) => {
      if (!isCascadingFilterKey(key)) return;

      const cascadingParams = getFilterCascadingParams(key);
      const cascadingSignature = getCascadingFilterParamsSignature(cascadingParams);
      const query = queries[key] ?? '';

      if (hasCascadingFilterParams(cascadingParams)) {
        if (!query && lastLoadedCascadingSignatureRef.current[key] === cascadingSignature) return;

        if (key === 'productIds') {
          await loadingProducts.refetch?.();
          return;
        }

        if (key === 'productFamilyIds') {
          await loadingProductFamilyIds.refetch?.();
          return;
        }

        const categoryIndex = cattegories.findIndex(category => category.value === key);
        await results[categoryIndex]?.refetch?.();
        return;
      }

      if (!query && initialCascadingDataRef.current[key]) {
        lastLoadedCascadingSignatureRef.current[key] = cascadingSignature;
        dispatch(SET_EXTRACTION_FILTERS_DATA_BY_KEY({ key, data: initialCascadingDataRef.current[key] ?? [] }));
        return;
      }

      if (key === 'productIds') {
        await loadingProducts.refetch?.();
        return;
      }

      if (key === 'productFamilyIds') {
        await loadingProductFamilyIds.refetch?.();
        return;
      }

      const categoryIndex = cattegories.findIndex(category => category.value === key);
      await results[categoryIndex]?.refetch?.();
    },
    [cattegories, dispatch, getFilterCascadingParams, loadingProductFamilyIds, loadingProducts, queries, results],
  );

  const loading = useMemo(
    () => ({
      productIds: checkIfIsLoading(loadingProducts.fetchStatus),
      storeIds: checkIfIsLoading(loadingStoreIds.fetchStatus),
      clusters: checkIfIsLoading(loadingClusters.fetchStatus),
      productFamilyIds: checkIfIsLoading(loadingProductFamilyIds.fetchStatus),
      segmentos: checkIfIsLoading(loadingSegmentos.fetchStatus),
      storeTypes: checkIfIsLoading(loadingStoreTypes.fetchStatus),
      cattegories: results.some(({ fetchStatus }) => checkIfIsLoading(fetchStatus)),
    }),
    [loadingProducts, loadingStoreIds, loadingClusters, loadingProductFamilyIds, loadingSegmentos, loadingStoreTypes, results],
  );

  return {
    data,
    loading,
    value,
    queries,
    cattegories,
    actions: { handleSearch, handleClean, handleChange, handleCleanAll, handleSelectAll, handleOpen, handleConfirmPasteProducts },
  };
};
